import React, { useEffect, useRef } from 'react';
import { FormattedMessage } from 'react-intl';
import { Box, Button, Paper, Stack, SxProps, Typography } from '@mui/material';
import { useRive } from '@rive-app/react-webgl2';

import { Group } from '../../organisms/group';
import { colors } from '../../theme/palette';
import { useSpotWizardDialog } from './spot-wizard-dialog';
import { PatternLight } from '../../atoms/pattern-light';

const whiteRiv = new URL(
  '../../static/animations/mr-battery-waving/white.riv',
  import.meta.url
);
const animationTimeline = 'Body Moving';
const animationDuration = 3000;

type Props = {
  sx?: SxProps;
};

export const SpotWizardBanner = ({ sx = {} }: Props) => {
  const { dialog, open } = useSpotWizardDialog();

  const containerRef = useRef<HTMLDivElement>(null);

  const { RiveComponent, rive } = useRive({
    src: whiteRiv.href,
    animations: animationTimeline,
    autoplay: false,
  });

  useEffect(() => {
    if (!containerRef.current || !rive) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          rive.play();
          setTimeout(() => {
            rive.pause();
          }, animationDuration);
          observer.disconnect();
        }
      },
      { threshold: 0.3 }
    );

    observer.observe(containerRef.current);
    return () => observer.disconnect();
  }, [rive]);

  return (
    <>
      <Group layout="list" sx={{ ...sx }}>
        <Paper
          ref={containerRef}
          sx={{
            bgcolor: (theme) =>
              theme.palette.mode === 'light' ? 'text.disabled' : '#343e42',
            color: colors.white,
            position: 'relative',

            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',

            overflow: 'hidden',
          }}
        >
          <Stack gap={1}>
            <Typography variant="h3" sx={{ color: 'common.white' }}>
              <FormattedMessage
                id="spot_wizard.banner.title"
                defaultMessage="Tápete? Poradím Vám"
              />
            </Typography>

            <Typography
              variant="body1"
              sx={{
                maxWidth: 400,
                mb: 2,
                color: 'common.white',
              }}
            >
              <FormattedMessage
                id="spot_wizard.banner.subtitle"
                defaultMessage="Průvodce vám pomůže s nastavením spotového obchodování."
              />
            </Typography>

            <Box>
              <Button
                variant="contained"
                size="small"
                onClick={open}
                sx={{ textTransform: 'none' }}
              >
                <FormattedMessage
                  id="spot_wizard.banner.button"
                  defaultMessage="Chci poradit"
                />
              </Button>
            </Box>
          </Stack>

          <Box
            sx={{
              alignSelf: 'stretch',
              width: 100,
              position: 'relative',
              flexShrink: 0,
            }}
          >
            <Box sx={{ inset: -35, top: -28, position: 'absolute' }}>
              <RiveComponent />
            </Box>
          </Box>

          <PatternLight
            sx={{ position: 'absolute', height: '90%', bottom: 0, right: -80 }}
          />
        </Paper>
      </Group>

      {dialog}
    </>
  );
};
